import api from './api';
import API_CONFIG from '@/config/api';
import type { AxiosResponse } from 'axios';
import type {
  MenuItem,
  MenuItemVariant,
  MenuCategory,
  ModifierGroup,
  MenuItemModifierGroup,
  MenuItemModifierGroupInput,
} from '@/types/menu';
import { safeStorage } from '@/utils/storage';

export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  error?: string;
}

export interface Category {
  id: string | number;
  name: string;
  description?: string;
}

export interface MenuItemResponse {
  id: string | number;
  name: string;
  description?: string;
  price: number | string;
  discount_price?: number;
  category: Category;
  is_available?: boolean;
  isAvailable?: boolean;
  image_url?: string;
  modifier_groups?: MenuItemModifierGroup[];
  variants?: MenuItemVariant[];
  created_at?: string;
  updated_at?: string;
}

// Use the configured API instance from api.ts
const apiInstance = api;

// Get menu base path from config
const MENU_BASE_PATH = API_CONFIG.ENDPOINTS.MENU || '/api/menu';
const CATEGORIES_BASE_PATH = '/categories';
const MODIFIER_GROUPS_PATH = `${MENU_BASE_PATH}/modifier-groups`;

// Add request interceptor to include auth token
apiInstance.interceptors.request.use(
  (config) => {
    const token = safeStorage.getItem('access_token') || safeStorage.getItem('access_token', true);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle errors
apiInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized access - please log in');
    }
    return Promise.reject(error);
  }
);

// Helper to normalize menu item data
const normalizeMenuItem = (item: any): MenuItemResponse => {
  // Ensure variants is always an array and has consistent property names
  const variants = Array.isArray(item.variants) ? item.variants.map((v: any) => ({
    id: v.id,
    name: v.name,
    price: Number(v.price),
    discount_price: v.discount_price ? Number(v.discount_price) : undefined,
    is_available: v.is_available ?? v.isAvailable ?? true,
    isAvailable: v.isAvailable ?? v.is_available ?? true
  })) : [];

  return {
    id: item.id,
    name: item.name,
    description: item.description,
    price: Number(item.price),
    discount_price: item.discount_price ? Number(item.discount_price) : undefined,
    category: item.category,
    is_available: item.is_available ?? item.isAvailable ?? true,
    isAvailable: item.isAvailable ?? item.is_available ?? true,
    image_url: item.image_url ?? item.imageUrl,
    modifier_groups: Array.isArray(item.modifier_groups) ? item.modifier_groups : [],
    variants,
    created_at: item.created_at,
    updated_at: item.updated_at
  };
};

export const getModifierGroups = async (includeInactive = false): Promise<ModifierGroup[]> => {
  const response = await apiInstance.get<ModifierGroup[]>(MODIFIER_GROUPS_PATH, {
    params: { include_inactive: includeInactive }
  });
  const groups = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
  return groups.map((group: any) => ({
    ...group,
    options: Array.isArray(group.options) ? group.options : []
  }));
};

export const createModifierGroup = async (data: Omit<ModifierGroup, 'id'>): Promise<ModifierGroup> => {
  const response = await apiInstance.post<ModifierGroup>(MODIFIER_GROUPS_PATH, data);
  return (response.data || response) as ModifierGroup;
};

export const updateModifierGroup = async (
  groupId: number,
  data: Partial<Omit<ModifierGroup, 'id'>>
): Promise<ModifierGroup> => {
  const response = await apiInstance.put<ModifierGroup>(`${MODIFIER_GROUPS_PATH}/${groupId}`, data);
  return (response.data || response) as ModifierGroup;
};

export const deleteModifierGroup = async (groupId: number): Promise<void> => {
  await apiInstance.delete(`${MODIFIER_GROUPS_PATH}/${groupId}`);
};

export const getModifierGroup = async (groupId: number): Promise<ModifierGroup> => {
  const response = await apiInstance.get<ModifierGroup>(`${MODIFIER_GROUPS_PATH}/${groupId}`);
  const group = response.data || response;
  return {
    ...group,
    options: Array.isArray(group.options) ? group.options : []
  };
};

// Menu Item Operations

export const getMenuItems = async (categoryId?: number, available?: boolean, search?: string): Promise<MenuItemResponse[]> => {
  const params: Record<string, string | number | boolean> = {};
  if (categoryId !== undefined) params.category_id = categoryId;
  if (available !== undefined) params.available = available;
  if (search && search.trim()) params.search = search.trim();
  
  const response = await apiInstance.get<MenuItemResponse[]>(
    MENU_BASE_PATH,
    { params }
  );
  
  // Get data from response.data (standard axios format)
  const items = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
  return items.map(normalizeMenuItem);
};

export const getMenuItem = async (id: string | number): Promise<MenuItemResponse> => {
  const response = await apiInstance.get<MenuItemResponse>(
    `${MENU_BASE_PATH}/${id}`
  );
  return normalizeMenuItem(response.data || response);
};

export const createMenuItem = async (menuItemData: Omit<MenuItem, 'id'>): Promise<MenuItemResponse> => {
  // Prepare the data with proper variant format and category_id
  const payload = {
    name: menuItemData.name,
    description: menuItemData.description,
    price: Number(menuItemData.price),
    discount_price: menuItemData.discount_price ? Number(menuItemData.discount_price) : undefined,
    category_id: menuItemData.category_id, // Send category_id instead of category name
    is_available: menuItemData.is_available ?? true,
    image_url: menuItemData.image_url,
    modifier_groups: Array.isArray(menuItemData.modifier_groups)
      ? (menuItemData.modifier_groups as MenuItemModifierGroupInput[]).map((groupLink) => ({
          group_id: groupLink.group_id,
          required: groupLink.required,
          min_select: groupLink.min_select,
          max_select: groupLink.max_select,
          sort_order: groupLink.sort_order ?? 0
        }))
      : [],
    // Ensure variants array is included if it exists
    variants: menuItemData.variants?.map(variant => ({
      name: variant.name,
      price: Number(variant.price),
      discount_price: variant.discount_price ? Number(variant.discount_price) : undefined,
      is_available: variant.is_available ?? true
    })) || []
  };

  const response = await apiInstance.post<MenuItemResponse>(
    MENU_BASE_PATH,
    payload
  );
  return normalizeMenuItem(response.data || response);
};

export const updateMenuItem = async (
  id: string | number,
  menuItemData: Partial<Omit<MenuItem, 'id'>>
): Promise<MenuItemResponse> => {
  // Prepare the data with proper variant format and category_id
  const payload: any = {};
  
  if (menuItemData.name !== undefined) payload.name = menuItemData.name;
  if (menuItemData.description !== undefined) payload.description = menuItemData.description;
  if (menuItemData.price !== undefined) payload.price = Number(menuItemData.price);
  if (menuItemData.discount_price !== undefined) {
    payload.discount_price = menuItemData.discount_price ? Number(menuItemData.discount_price) : undefined;
  }
  if (menuItemData.category_id !== undefined) payload.category_id = menuItemData.category_id; // Use category_id
  if (menuItemData.is_available !== undefined) payload.is_available = menuItemData.is_available;
  if (menuItemData.image_url !== undefined) payload.image_url = menuItemData.image_url;
  if (menuItemData.modifier_groups !== undefined) {
    payload.modifier_groups = (menuItemData.modifier_groups as MenuItemModifierGroupInput[]).map((groupLink) => ({
      group_id: groupLink.group_id,
      required: groupLink.required,
      min_select: groupLink.min_select,
      max_select: groupLink.max_select,
      sort_order: groupLink.sort_order ?? 0,
    }));
  }
  
  // Only include variants if they are provided in the update
  if (menuItemData.variants) {
    payload.variants = menuItemData.variants.map(variant => ({
      ...(variant.id && { id: variant.id }), // Only include id if it exists
      name: variant.name,
      price: Number(variant.price),
      discount_price: variant.discount_price ? Number(variant.discount_price) : undefined,
      is_available: variant.is_available ?? true
    }));
  }

  const response = await apiInstance.put<MenuItemResponse>(
    `${MENU_BASE_PATH}/${id}`,
    payload
  );
  return normalizeMenuItem(response.data || response);
};

export const deleteMenuItem = async (id: string | number): Promise<boolean> => {
  await apiInstance.delete(`${MENU_BASE_PATH}/${id}`);
  return true;
};

export const updateMenuItemAvailability = async (
  id: string | number,
  isAvailable: boolean
): Promise<MenuItemResponse> => {
  const response = await apiInstance.patch<MenuItemResponse>(
    `${MENU_BASE_PATH}/${id}/availability`,
    { is_available: isAvailable }
  );
  return normalizeMenuItem(response.data || response);
};

export const getCategories = async (search?: string): Promise<MenuCategory[]> => {
  const params = new URLSearchParams();
  if (search && search.trim()) params.append('search', search.trim());

  const response = await apiInstance.get<MenuCategory[]>(`${CATEGORIES_BASE_PATH}/`, { params });
  const arr = Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
  // Use spread operator to preserve all fields automatically
  return arr.map((c: any) => ({
    ...c,
    description: c.description || '',
    visible_in_kitchen: c.visible_in_kitchen ?? true
  } as MenuCategory));
};

// Category CRUD Operations

export const createCategory = async (data: { name: string; description?: string; visible_in_kitchen?: boolean }): Promise<MenuCategory> => {
  const payload = { 
    name: data.name, 
    description: data.description ?? '',
    visible_in_kitchen: data.visible_in_kitchen ?? true
  };
  const response = await apiInstance.post<MenuCategory>(`${CATEGORIES_BASE_PATH}/`, payload);
  return response as unknown as MenuCategory;
};

export const updateCategory = async (
  id: string | number,
  data: Partial<{ name: string; description?: string; visible_in_kitchen?: boolean }>
): Promise<MenuCategory> => {
  const response = await apiInstance.put<MenuCategory>(`${CATEGORIES_BASE_PATH}/${id}`, data);
  return response as unknown as MenuCategory;
};

export const deleteCategory = async (id: string | number): Promise<boolean> => {
  await apiInstance.delete(`${CATEGORIES_BASE_PATH}/${id}`);
  return true;
};

export const getCategory = async (id: string | number): Promise<MenuCategory> => {
  const response = await apiInstance.get<MenuCategory>(`${CATEGORIES_BASE_PATH}/${id}`);
  const category = response.data || response;
  return {
    ...category,
    description: category.description || '',
    visible_in_kitchen: category.visible_in_kitchen ?? true
  } as MenuCategory;
};

// Variant Operations

export const addMenuItemVariant = async (
  menuItemId: string | number,
  variant: Omit<MenuItemVariant, 'id'> & { is_available?: boolean }
): Promise<MenuItemVariant> => {
  // Ensure we're using snake_case for the API
  const variantData = {
    ...variant,
    is_available: variant.is_available ?? true
  };
  
  const response = await apiInstance.post<MenuItemVariant>(
    `${MENU_BASE_PATH}/${menuItemId}/variants`,
    variantData
  );
  
  // The response is already the variant data we need after the interceptor
  return response as unknown as MenuItemVariant;
};

export const updateMenuItemVariant = async (
  menuItemId: string | number,
  variantId: string | number,
  variantData: Partial<Omit<MenuItemVariant, 'id'>> & { isAvailable?: boolean }
): Promise<MenuItemVariant> => {
  const data = { ...variantData };
  if ('isAvailable' in data) {
    data.is_available = data.isAvailable;
    delete data.isAvailable;
  }
  
  const response = await apiInstance.put<ApiResponse<MenuItemVariant>>(
    `${MENU_BASE_PATH}/${menuItemId}/variants/${variantId}`,
    data
  );
  return response.data.data;
};

export const deleteMenuItemVariant = async (
  menuItemId: string | number,
  variantId: string | number
): Promise<boolean> => {
  await apiInstance.delete(`${MENU_BASE_PATH}/${menuItemId}/variants/${variantId}`);
  return true;
};

export const toggleVariantAvailability = async (
  menuItemId: string | number,
  variantId: string | number,
  isAvailable: boolean
): Promise<MenuItemVariant> => {
  return updateMenuItemVariant(menuItemId, variantId, { isAvailable });
};

// Export all functions as default
export default {
  // Menu Item operations
  getMenuItems,
  getMenuItem,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  updateMenuItemAvailability,
  getModifierGroups,
  getModifierGroup,
  createModifierGroup,
  updateModifierGroup,
  deleteModifierGroup,
  getCategories,
  getCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  
  // Variant operations
  addMenuItemVariant,
  updateMenuItemVariant,
  deleteMenuItemVariant,
  toggleVariantAvailability
};
